import {
  CHALLENGE_TYPES,
  isChallengeType,
  isChallengeUserRole,
} from '../../types/domain/challenge.types';
import { dayjs, formatDisplayDate } from '../date';
import { byDate, byNumber, byText, sortList, thenBy } from '../list-sort';
import { formatHandle } from '../share';

import type {
  ChallengeStatus,
  ChallengeSummary,
  ChallengeType,
  ChallengeUserRole,
} from '../../types/domain/challenge.types';
import type { Comparator, SortCatalog } from '../list-sort';

/**
 * Challenge List Model
 *
 * The Challenges list's derivations, shared by the web page and the mobile
 * screen so both read a challenge the same way: the status grouping, the
 * client-side filters, counts and hero pick, the badge and legend copy, and the
 * scoreboard figures. The race track's geometry lives beside it in
 * `race-track.ts`.
 *
 * The index endpoint returns the user's whole set in one response (see
 * `challenge.store`), so every filter here runs on the client.
 */

// =============================================================================
// TYPES
// =============================================================================

/**
 * Challenge status filter options (UI groupings of ChallengeStatus)
 *
 * - active: status === 'Active'
 * - upcoming: status === 'Pending'
 * - completed: status === 'Completed'
 * - all: everything, including 'Expired' and 'Cancelled'
 */
export type ChallengeStatusFilter = 'active' | 'upcoming' | 'completed' | 'all';

/**
 * UI grouping of the five ChallengeStatus values
 */
export type ChallengeStatusGroup =
  'active' | 'upcoming' | 'completed' | 'ended';

/**
 * A single entry from ChallengeSummary.top_participants
 */
export type ChallengeTopParticipant =
  ChallengeSummary['top_participants'][number];

/**
 * Challenge filter state for search and filtering
 */
export interface ChallengeFilterState {
  /** Status filter: active, upcoming, completed, or all challenges */
  status: ChallengeStatusFilter;
  /** Challenge type filter (optional) */
  type?: ChallengeType;
  /** Role filter: challenges I created vs joined (optional) */
  role?: ChallengeUserRole;
  /** Search query for title/description */
  search?: string;
}

/**
 * Counts for challenge status filter badges
 */
export interface ChallengeStatusCounts {
  /** Number of active challenges */
  active: number;
  /** Number of upcoming (pending) challenges */
  upcoming: number;
  /** Number of completed challenges */
  completed: number;
  /** Total number of challenges */
  total: number;
}

/**
 * One active-filter chip, as both platforms' filter bars draw it.
 */
export interface ChallengeFilterChip {
  /** The filter key the chip clears when dismissed */
  key: string;
  label: string;
}

/**
 * What the list shows for a set of filters: the matches, the challenge
 * featured above them, and the rest.
 */
export interface ChallengeListView {
  /** Every challenge the status tab and the filters leave */
  filtered: ChallengeSummary[];
  /** The most urgent active challenge (Active and All tabs only) */
  hero: ChallengeSummary | undefined;
  /** The filtered challenges, less the hero */
  list: ChallengeSummary[];
}

/**
 * Tone for the "you" side of the track legend
 */
export type LegendTone = 'accent' | 'success' | 'muted';

/**
 * Which status badge a challenge wears. Each platform maps a kind to its own
 * badge; the label is shared.
 */
export type ChallengeStatusBadgeKind =
  'active' | 'upcoming' | 'won' | 'completed' | 'expired' | 'cancelled';

/**
 * Who is looking at a challenge, for the words that differ by viewer: its
 * creator is the one who starts it, everyone else waits on them.
 */
export type ChallengeViewer =
  { role: 'creator' } | { role: 'participant'; creatorUsername: string };

/**
 * How loudly a deadline speaks: `critical` at three days or fewer (past due
 * included), `warning` within a fortnight, `calm` otherwise.
 */
export type ChallengeDeadlineTone = 'critical' | 'warning' | 'calm';

/**
 * The urgency badge's treatment. `past` is the calm "Time's up" of a race
 * whose window has closed — there is no urgency left to communicate.
 */
export type ChallengeUrgencyTone = 'critical' | 'warning' | 'past';

// =============================================================================
// FILTER VOCABULARY
// =============================================================================

/** The status tabs, in order. */
export const CHALLENGE_STATUS_FILTERS: ReadonlyArray<{
  value: ChallengeStatusFilter;
  label: string;
}> = [
  { value: 'active', label: 'Active' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' },
];

/** The role filter's option names. */
export const CHALLENGE_ROLE_LABELS: Record<ChallengeUserRole, string> = {
  creator: 'Created by me',
  participant: 'Joined',
};

/** Filter keys, as the filter bars report them. */
export const CHALLENGE_FILTER_KEYS = {
  search: 'search',
  type: 'type',
  role: 'role',
} as const;

/** The type filter's options, in picker order. */
export const CHALLENGE_TYPE_OPTIONS: ReadonlyArray<{
  value: ChallengeType;
  label: string;
}> = CHALLENGE_TYPES.map((type) => ({
  value: type,
  label: getChallengeTypeLabel(type),
}));

/** The role filter's options. */
export const CHALLENGE_ROLE_OPTIONS: ReadonlyArray<{
  value: ChallengeUserRole;
  label: string;
}> = (Object.keys(CHALLENGE_ROLE_LABELS) as ChallengeUserRole[]).map(
  (role) => ({ value: role, label: CHALLENGE_ROLE_LABELS[role] })
);

/** The screen opens on the races being run. */
export const DEFAULT_CHALLENGE_FILTERS: ChallengeFilterState = {
  status: 'active',
};

/** A status tab's count. The All tab counts every challenge. */
export function getStatusFilterCount(
  counts: ChallengeStatusCounts,
  status: ChallengeStatusFilter
): number {
  return status === 'all' ? counts.total : counts[status];
}

/** A tab id as a status, falling back to the default for anything unknown. */
export function toChallengeStatusFilter(value: string): ChallengeStatusFilter {
  return (
    CHALLENGE_STATUS_FILTERS.find((filter) => filter.value === value)?.value ??
    DEFAULT_CHALLENGE_FILTERS.status
  );
}

// =============================================================================
// FILTER STATE
// =============================================================================

/** The filter state as a filter bar's values. */
export function toChallengeFilterValues(
  filters: ChallengeFilterState
): Record<string, string> {
  const values: Record<string, string> = {};
  if (filters.search) values[CHALLENGE_FILTER_KEYS.search] = filters.search;
  if (filters.type) values[CHALLENGE_FILTER_KEYS.type] = filters.type;
  if (filters.role) values[CHALLENGE_FILTER_KEYS.role] = filters.role;
  return values;
}

/**
 * The filter state after a filter bar reports a change. An empty, missing or
 * unrecognised value clears that filter — which is how a dismissed chip
 * (`undefined`) arrives.
 */
export function applyChallengeFilterChange(
  filters: ChallengeFilterState,
  key: string,
  value: unknown
): ChallengeFilterState {
  const next: ChallengeFilterState = { ...filters };

  if (key === CHALLENGE_FILTER_KEYS.search) {
    if (typeof value === 'string' && value !== '') next.search = value;
    else delete next.search;
  } else if (key === CHALLENGE_FILTER_KEYS.type) {
    if (isChallengeType(value)) next.type = value;
    else delete next.type;
  } else if (key === CHALLENGE_FILTER_KEYS.role) {
    if (isChallengeUserRole(value)) next.role = value;
    else delete next.role;
  }

  return next;
}

/**
 * The filters cleared back to the status tab. `keepSearch` leaves the search
 * too — for a bar whose search field sits outside the menu it clears, and has
 * its own clear button.
 */
export function clearChallengeFilters(
  filters: ChallengeFilterState,
  { keepSearch = false }: { keepSearch?: boolean } = {}
): ChallengeFilterState {
  return keepSearch && filters.search
    ? { status: filters.status, search: filters.search }
    : { status: filters.status };
}

/**
 * The active filters as dismissible chips. The status is never a chip (the
 * tabs show it); leave the search out where its field already shows it.
 */
export function getChallengeFilterChips(
  filters: ChallengeFilterState,
  { includeSearch = true }: { includeSearch?: boolean } = {}
): ChallengeFilterChip[] {
  const chips: ChallengeFilterChip[] = [];

  if (includeSearch && filters.search) {
    chips.push({
      key: CHALLENGE_FILTER_KEYS.search,
      label: `"${filters.search}"`,
    });
  }
  if (filters.type) {
    chips.push({
      key: CHALLENGE_FILTER_KEYS.type,
      label: getChallengeTypeLabel(filters.type),
    });
  }
  if (filters.role) {
    chips.push({
      key: CHALLENGE_FILTER_KEYS.role,
      label: CHALLENGE_ROLE_LABELS[filters.role],
    });
  }

  return chips;
}

/** Whether the search or a filter is narrowing the list. */
export function isFilteringChallenges(filters: ChallengeFilterState): boolean {
  return Boolean(filters.search || filters.type || filters.role);
}

// =============================================================================
// FILTERING, COUNTS AND THE HERO
// =============================================================================

/**
 * Map a ChallengeStatus to its UI status group
 */
export function getStatusGroup(status: ChallengeStatus): ChallengeStatusGroup {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Pending':
      return 'upcoming';
    case 'Completed':
      return 'completed';
    default:
      return 'ended';
  }
}

/**
 * Whether a challenge matches the given status filter tab
 */
function matchesStatusFilter(
  challenge: ChallengeSummary,
  status: ChallengeStatusFilter
): boolean {
  if (status === 'all') return true;
  return getStatusGroup(challenge.status) === status;
}

/**
 * Filter challenges based on the current filter state
 */
export function filterChallenges(
  challenges: ChallengeSummary[],
  filters: ChallengeFilterState
): ChallengeSummary[] {
  let filtered = challenges.filter((challenge) =>
    matchesStatusFilter(challenge, filters.status)
  );

  if (filters.type) {
    filtered = filtered.filter((challenge) => challenge.type === filters.type);
  }

  if (filters.role) {
    filtered = filtered.filter(
      (challenge) => challenge.user_role === filters.role
    );
  }

  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    // The API can send a null title (the name stands in) or description, so
    // search what the card shows rather than the raw fields.
    filtered = filtered.filter(
      (challenge) =>
        getChallengeTitle(challenge).toLowerCase().includes(searchLower) ||
        (challenge.description ?? '').toLowerCase().includes(searchLower)
    );
  }

  return filtered;
}

/**
 * Calculate status counts for the filter toggle badges
 */
export function getChallengeStatusCounts(
  challenges: ChallengeSummary[]
): ChallengeStatusCounts {
  const counts: ChallengeStatusCounts = {
    active: 0,
    upcoming: 0,
    completed: 0,
    total: challenges.length,
  };

  challenges.forEach((challenge) => {
    const group = getStatusGroup(challenge.status);
    if (group === 'active') counts.active++;
    else if (group === 'upcoming') counts.upcoming++;
    else if (group === 'completed') counts.completed++;
  });

  return counts;
}

/**
 * Pick the featured hero challenge: the active challenge closest to its
 * deadline. Returns undefined when there are no active challenges.
 */
export function findHeroChallenge(
  challenges: ChallengeSummary[]
): ChallengeSummary | undefined {
  return challenges
    .filter((challenge) => challenge.status === 'Active')
    .reduce<ChallengeSummary | undefined>(
      (mostUrgent, challenge) =>
        !mostUrgent || challenge.days_remaining < mostUrgent.days_remaining
          ? challenge
          : mostUrgent,
      undefined
    );
}

/**
 * What the list shows for these filters, in `sort` order. The hero is featured
 * on the Active and All tabs only — on a status-specific tab it would pull an
 * active challenge into a list the user asked not to see — and is left out of
 * the list below it. It keeps its own rule (nearest deadline) whatever the sort.
 */
export function getChallengeListView(
  challenges: ChallengeSummary[],
  filters: ChallengeFilterState,
  sort: ChallengeSortOrder = DEFAULT_CHALLENGE_SORT
): ChallengeListView {
  const filtered = sortList(
    filterChallenges(challenges, filters),
    CHALLENGE_SORTS,
    sort
  );
  const hero =
    filters.status === 'active' || filters.status === 'all'
      ? findHeroChallenge(filtered)
      : undefined;
  const list = hero
    ? filtered.filter((challenge) => challenge.id !== hero.id)
    : filtered;

  return { filtered, hero, list };
}

/**
 * What the empty slot says when the tab and filters leave nothing, so the
 * filter explains itself instead of repeating "no challenges".
 */
export function getNoMatchMessage(status: ChallengeStatusFilter): string {
  return status === 'active'
    ? 'You have no active challenges. Switch to "All" to see every challenge.'
    : 'No challenges match your current filters. Try adjusting your search.';
}

// =============================================================================
// IDENTITY
// =============================================================================

/**
 * Display title for a challenge, falling back to `name` while the API
 * migrates to the new `title` field.
 */
export function getChallengeTitle(challenge: ChallengeSummary): string {
  return challenge.title || challenge.name;
}

/**
 * Short display label for a challenge type ("Plan-Based Challenge" -> "Plan-based")
 */
export function getChallengeTypeLabel(type: ChallengeSummary['type']): string {
  const base = type.replace(' Challenge', '');
  return `${base.charAt(0)}${base.slice(1).toLowerCase()}`;
}

/**
 * Creator display name ("you" when the current user created the challenge)
 */
export function getCreatorLabel(challenge: ChallengeSummary): string {
  return challenge.user_role === 'creator' ? 'you' : challenge.creator.username;
}

/**
 * Participant count line for the creator row, phrased per status group.
 * Returns an empty string while the API doesn't supply the count.
 */
export function getParticipantsLine(challenge: ChallengeSummary): string {
  const group = getStatusGroup(challenge.status);
  const count = challenge.participants;

  if (count == null) return '';
  if (group === 'upcoming') return `${count} at the start line`;
  if (group === 'active') return `${count} racing`;
  if (group === 'completed') return `${count} raced`;
  return `${count} participants`;
}

/** A card's byline: "by you · 5 racing". */
export function getCardByline(challenge: ChallengeSummary): string {
  const participants = getParticipantsLine(challenge);
  return `by ${getCreatorLabel(challenge)}${participants ? ` · ${participants}` : ''}`;
}

/** The hero's byline, beside its avatar stack: "5 racing · by you". */
export function getHeroByline(challenge: ChallengeSummary): string {
  const participants = getParticipantsLine(challenge);
  return `${participants ? `${participants} · ` : ''}by ${getCreatorLabel(challenge)}`;
}

// =============================================================================
// PROGRESS AND DATES
// =============================================================================

/**
 * Coerce a progress value to a number. The API may send progress as a
 * numeric string (e.g. "87.82"), so comparisons and rounding must not
 * rely on the declared `number` type.
 */
export function toProgressNumber(value: number | string): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Rounded progress percentage for display (API may return decimals or
 * numeric strings).
 */
export function getProgressPercent(challenge: ChallengeSummary): number {
  return Math.round(toProgressNumber(challenge.progress_percentage));
}

/**
 * Days remaining, clamped so an Active challenge that the backend hasn't
 * yet flipped to Expired (negative days) never renders a negative count.
 */
export function getDaysRemaining(challenge: ChallengeSummary): number {
  return Math.max(challenge.days_remaining, 0);
}

/**
 * Deadline copy for the hero eyebrow.
 *
 * "Ends today" is reserved for an exact zero. A past-due (negative) active
 * challenge — one the backend hasn't yet flipped to Expired — reads
 * "Time's up" rather than claiming it still ends today.
 */
export function getDeadlineLabel(daysRemaining: number): string {
  if (daysRemaining < 0) return "Time's up";
  if (daysRemaining === 0) return 'Ends today';
  if (daysRemaining === 1) return 'Ends tomorrow';
  return `Ends in ${daysRemaining} days`;
}

/**
 * How loudly the hero's deadline eyebrow speaks.
 */
export function getDeadlineTone(daysRemaining: number): ChallengeDeadlineTone {
  if (daysRemaining <= 3) return 'critical';
  if (daysRemaining <= 14) return 'warning';
  return 'calm';
}

/**
 * Format the challenge date range for card footers (e.g. "1 Jun – 24 Jun")
 */
export function formatDateRange(startsAt: string, endsAt: string): string {
  const start = formatDisplayDate(startsAt, 'dateShort');
  const end = formatDisplayDate(endsAt, 'dateShort');
  return start && end ? `${start} – ${end}` : '';
}

/**
 * Days from today to a challenge's start date: negative once it has passed.
 * Only the creator starts a challenge, so a Pending one can sit past its date.
 */
export function getDaysToStartDate(startsAt: string): number {
  return dayjs(startsAt).startOf('day').diff(dayjs().startOf('day'), 'day');
}

/**
 * Days until a pending challenge starts (clamped at 0)
 */
export function getDaysUntilStart(startsAt: string): number {
  return Math.max(getDaysToStartDate(startsAt), 0);
}

/** The viewer of a challenge the API sent, from its `user_role`. */
export function getChallengeViewer(
  challenge: Pick<ChallengeSummary, 'user_role' | 'creator'>
): ChallengeViewer {
  return challenge.user_role === 'creator'
    ? { role: 'creator' }
    : { role: 'participant', creatorUsername: challenge.creator.username };
}

/**
 * Challenge duration in whole weeks (at least one). `ends_on` is the last day of
 * the challenge, so the span runs to the start of the day after it.
 */
export function getDurationWeeks(startsAt: string, endsAt: string): number {
  return Math.max(dayjs(endsAt).add(1, 'day').diff(dayjs(startsAt), 'week'), 1);
}

// =============================================================================
// RACERS
// =============================================================================

/** How many people the hero's avatar stack shows. */
export const HERO_AVATAR_LIMIT = 5;

/** How many competitors race beside you on the hero's (wider) track. */
export const HERO_COMPETITOR_LIMIT = 4;

/**
 * Top competitors for the ghost markers, excluding the current user.
 * The API returns `top_participants` already ordered by progress, so the
 * first entries are the front-runners.
 */
export function getTopCompetitors(
  challenge: ChallengeSummary,
  max = 3
): ChallengeTopParticipant[] {
  return (challenge.top_participants ?? [])
    .filter((participant) => !participant.is_current_user)
    .slice(0, max);
}

/** The people in the hero's avatar stack, front-runners first. */
export function getHeroParticipants(
  challenge: ChallengeSummary
): ChallengeTopParticipant[] {
  return (challenge.top_participants ?? []).slice(0, HERO_AVATAR_LIMIT);
}

/** The current user's username on the track, or "You" when not listed. */
export function getRacerUsername(challenge: ChallengeSummary): string {
  return (
    challenge.top_participants?.find(
      (participant) => participant.is_current_user
    )?.username ?? 'You'
  );
}

// =============================================================================
// LEGENDS
// =============================================================================

/**
 * Left-hand track legend: the current user's position
 */
export function getYouLegend(challenge: ChallengeSummary): {
  text: string;
  tone: LegendTone;
} {
  const group = getStatusGroup(challenge.status);

  if (group === 'upcoming') {
    return { text: "Race hasn't started", tone: 'muted' };
  }
  const progress = getProgressPercent(challenge);

  if (group === 'completed') {
    return challenge.rank === 1
      ? { text: 'You won the race', tone: 'success' }
      : { text: `You finished — ${progress}%`, tone: 'success' };
  }
  if (challenge.status === 'Expired') {
    return { text: `Time ran out at ${progress}%`, tone: 'muted' };
  }
  if (challenge.status === 'Cancelled') {
    return { text: `Race called off at ${progress}%`, tone: 'muted' };
  }
  return { text: `You — ${progress}%`, tone: 'accent' };
}

/**
 * Right-hand track legend: the leader (or contextual info when there
 * is no race to lead).
 */
export function getLeaderLegend(challenge: ChallengeSummary): string {
  const group = getStatusGroup(challenge.status);

  if (group === 'upcoming') {
    return `${getDurationWeeks(challenge.starts_on, challenge.ends_on)}-week challenge`;
  }
  if (group === 'ended') {
    const endedOn = formatDisplayDate(challenge.ends_on, 'dateShort');
    return endedOn ? `Ended ${endedOn}` : 'Ended';
  }

  // top_participants is ordered by progress (highest first), so the first
  // entry is the race leader.
  const leader = (challenge.top_participants ?? [])[0];
  if (!leader) {
    return '';
  }
  if (leader.is_current_user) {
    const runnerUp = (challenge.top_participants ?? [])[1];
    return runnerUp
      ? `Runner-up ${runnerUp.username} — ${Math.round(toProgressNumber(runnerUp.progress))}%`
      : 'You lead the race';
  }
  return `Leader ${leader.username} — ${Math.round(toProgressNumber(leader.progress))}%`;
}

// =============================================================================
// BADGES
// =============================================================================

/**
 * The status badge a challenge wears, and its words.
 *
 * - Active: the sakura dot
 * - Pending: the start date ("Starts 3 Jul"), or "Upcoming" without one. From
 *   the start date on, it says who the race waits on — "Ready to start" for
 *   its creator, "Waiting for @maya" for everyone else — when the viewer is known
 * - Completed: "Won · 1st place" for the winner, else "Completed"
 * - Expired / Cancelled: the muted end states
 */
export function getChallengeStatusBadge(
  status: ChallengeStatus,
  startsAt?: string,
  rank?: number,
  viewer?: ChallengeViewer
): { kind: ChallengeStatusBadgeKind; label: string } {
  switch (status) {
    case 'Active':
      return { kind: 'active', label: 'Active' };
    case 'Pending': {
      if (viewer && startsAt && getDaysUntilStart(startsAt) === 0) {
        return {
          kind: 'upcoming',
          label:
            viewer.role === 'creator'
              ? 'Ready to start'
              : `Waiting for ${formatHandle(viewer.creatorUsername)}`,
        };
      }
      const startsOn = formatDisplayDate(startsAt, 'dateShort');
      return {
        kind: 'upcoming',
        label: startsOn ? `Starts ${startsOn}` : 'Upcoming',
      };
    }
    case 'Completed':
      return rank === 1
        ? { kind: 'won', label: 'Won · 1st place' }
        : { kind: 'completed', label: 'Completed' };
    case 'Expired':
      return { kind: 'expired', label: 'Expired' };
    case 'Cancelled':
      return { kind: 'cancelled', label: 'Cancelled' };
  }
}

/**
 * The days-remaining urgency badge for an active challenge, or null when a
 * fortnight or more is left (no urgency to communicate).
 *
 * - past due (negative): a calm "Time's up" — the race window has closed
 * - ends today / 3 days or fewer: critical
 * - 14 days or fewer: warning
 */
export function getUrgencyBadge(
  daysRemaining: number
): { label: string; tone: ChallengeUrgencyTone } | null {
  if (daysRemaining > 14) return null;
  if (daysRemaining < 0) return { label: "Time's up", tone: 'past' };

  const label =
    daysRemaining === 0
      ? 'Ends today'
      : daysRemaining === 1
        ? '1 day left'
        : `${daysRemaining} days left`;

  return { label, tone: daysRemaining <= 3 ? 'critical' : 'warning' };
}

// =============================================================================
// SCOREBOARD
// =============================================================================

/** The hero's Days left figure, two digits ("04"). */
export function formatDaysLeft(challenge: ChallengeSummary): string {
  return String(getDaysRemaining(challenge)).padStart(2, '0');
}

/** A rank as the scoreboard prints it: "#2", or "—" before there is one. */
export function formatChallengeRank(rank: number | null | undefined): string {
  return rank ? `#${rank}` : '—';
}

/**
 * Points as the scoreboard prints them, grouped ("1,240"). Points are whole
 * counts, so they are grouped rather than rounded (`formatDisplayValue` is for
 * measured values).
 */
export function formatChallengePoints(
  points: number | null | undefined
): string {
  return (points ?? 0).toLocaleString();
}

/**
 * Whether a card's footer shows the user's rank and points — once the race
 * has started and the user has placed — rather than the field size.
 */
export function showsChallengeRank(challenge: ChallengeSummary): boolean {
  return (
    (challenge.rank ?? 0) > 0 && getStatusGroup(challenge.status) !== 'upcoming'
  );
}

// =============================================================================
// SORTING
// =============================================================================

export type ChallengeSortOrder =
  'ending' | 'starting' | 'newest' | 'oldest' | 'name' | 'racing' | 'rank';

const byChallengeTitle = byText<ChallengeSummary>(getChallengeTitle);

/** A race that is over goes after every race still running or to come. */
const byStillOn: Comparator<ChallengeSummary> = (a, b) => {
  const over = (challenge: ChallengeSummary): number => {
    const group = getStatusGroup(challenge.status);
    return group === 'completed' || group === 'ended' ? 1 : 0;
  };
  return over(a) - over(b);
};

/**
 * The Challenges list's sorts. A tie falls back to the title. Ending soonest
 * is the default: the hero's own rule, so the list reads on from it.
 */
export const CHALLENGE_SORTS: SortCatalog<
  ChallengeSummary,
  ChallengeSortOrder
> = {
  ending: {
    label: 'Ending soonest',
    compare: thenBy(
      byStillOn,
      byDate((challenge) => challenge.ends_on, 'asc'),
      byChallengeTitle
    ),
  },
  starting: {
    label: 'Starting soonest',
    compare: thenBy(
      byStillOn,
      byDate((challenge) => challenge.starts_on, 'asc'),
      byChallengeTitle
    ),
  },
  newest: {
    label: 'Newest',
    compare: thenBy(
      byDate((challenge) => challenge.created_at, 'desc'),
      byChallengeTitle
    ),
  },
  oldest: {
    label: 'Oldest',
    compare: thenBy(
      byDate((challenge) => challenge.created_at, 'asc'),
      byChallengeTitle
    ),
  },
  name: { label: 'Name A–Z', compare: byChallengeTitle },
  racing: {
    label: 'Most racing',
    compare: thenBy(
      byNumber((challenge) => challenge.participants, 'desc'),
      byChallengeTitle
    ),
  },
  rank: {
    label: 'Best rank',
    // Unranked (an invite, a race not yet scored) goes last.
    compare: thenBy(
      byNumber((challenge) => challenge.rank, 'asc'),
      byChallengeTitle
    ),
  },
};

export const DEFAULT_CHALLENGE_SORT: ChallengeSortOrder = 'ending';
